/**
 * Boot Scene
 * 
 * First scene that loads. Performs minimal initialization:
 * - Device detection
 * - SDK initialization
 * - Transition to PreloadScene
 * 
 * This scene should be as fast as possible (<100ms)
 */

import Phaser from 'phaser';
import { SCENES } from '../config/Constants';
import { PlatformManager } from '../sdk/PlatformManager';

export class BootScene extends Phaser.Scene {
  constructor() {
    super({ key: SCENES.BOOT });
  }
  
  /**
   * Preload - Load only essential boot assets
   */
  preload(): void {
    // No assets loaded here - keep boot instant
  }
  
  /**
   * Create - Initialize minimum requirements
   */
  create(): void {
    console.log('[BootScene] Starting...');
    
    // Set up game scale events
    this.scale.on('resize', this.handleResize, this);
    
    // Check device capabilities
    this.detectDeviceCapabilities();
    
    // Initialize registry with default values
    this.initializeRegistry();
    
    // Start preload scene immediately
    this.scene.start(SCENES.PRELOAD);
    
    console.log('[BootScene] Complete, starting PreloadScene');
  }
  
  /**
   * Detect device capabilities and store in registry
   */
  private detectDeviceCapabilities(): void {
    const capabilities = {
      // Check if touch device
      isTouch: this.sys.game.device.input.touch,
      
      // Check if mobile device
      isMobile: this.sys.game.device.os.android || 
                this.sys.game.device.os.iOS ||
                this.sys.game.device.os.windowsPhone,
      
      // Check WebGL support
      hasWebGL: this.sys.game.renderer.type === Phaser.WEBGL,
      
      // Check audio support
      hasWebAudio: this.sys.game.device.audio.webAudio,
      
      // Detect low-end device
      isLowEnd: this.detectLowEndDevice(),
      
      // Current platform
      platform: PlatformManager.getInstance().getPlatform()
    };
    
    // Store in registry for global access
    this.registry.set('capabilities', capabilities);
    
    console.log('[BootScene] Device capabilities:', capabilities);
  }
  
  /**
   * Detect if this is a low-end device
   */
  private detectLowEndDevice(): boolean {
    // Check device memory (if available)
    const nav = navigator as Navigator & { deviceMemory?: number };
    if (nav.deviceMemory && nav.deviceMemory < 4) {
      return true;
    }
    
    // Check hardware concurrency
    if (navigator.hardwareConcurrency && navigator.hardwareConcurrency < 4) {
      return true;
    }
    
    // Check if Android (generally lower performance)
    if (this.sys.game.device.os.android) {
      return true;
    }
    
    return false;
  }
  
  /**
   * Initialize registry with default game state
   */
  private initializeRegistry(): void {
    // High score from localStorage
    const savedHighScore = localStorage.getItem('sticky_planet_high_score');
    this.registry.set('highScore', savedHighScore ? parseInt(savedHighScore, 10) : 0);
    
    // Sound settings from localStorage
    const soundEnabled = localStorage.getItem('sticky_planet_sound');
    this.registry.set('soundEnabled', soundEnabled !== 'false');
    
    // Music settings from localStorage
    const musicEnabled = localStorage.getItem('sticky_planet_music');
    this.registry.set('musicEnabled', musicEnabled !== 'false');
    
    // Colorblind mode from localStorage
    const colorblindMode = localStorage.getItem('sticky_planet_colorblind');
    this.registry.set('colorblindMode', colorblindMode === 'true');
    
    // Games played counter
    const gamesPlayed = localStorage.getItem('sticky_planet_games_played');
    this.registry.set('gamesPlayed', gamesPlayed ? parseInt(gamesPlayed, 10) : 0);
    
    // Current score (reset each game)
    this.registry.set('currentScore', 0);
    
    // Current combo (reset each game)
    this.registry.set('currentCombo', 0);
  }
  
  /**
   * Handle resize events
   */
  private handleResize(): void {
    // Emit resize event for other scenes to handle
    this.events.emit('resize');
  }
}

export default BootScene;
